import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, type CrmExportScope, getErrorMessage } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { isJobActive } from "../../../utils/status.ts";

interface CrmExportDialogProps {
  open: boolean;
  onClose: () => void;
  listId: string;
}

const SCOPE_LABELS: Record<CrmExportScope, string> = {
  not_disqualified: "Everything except disqualified",
  accepted: "Accepted only",
  all: "All entries",
};

export default function CrmExportDialog({ open, onClose, listId }: CrmExportDialogProps) {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<CrmExportScope>("not_disqualified");
  const [includeContacts, setIncludeContacts] = useState(true);
  const [createCrmList, setCreateCrmList] = useState(true);
  const [enrollInSequence, setEnrollInSequence] = useState(false);
  const [sequenceId, setSequenceId] = useState("");

  // Only the connection id is needed here, so this asks for that alone rather
  // than the full CRM status, which returns a row per contact in the list.
  const connection = useQuery({
    queryKey: keys.prospectListCrmConnection.byList(listId),
    queryFn: () => api.getProspectListCrmConnection(listId),
    enabled: open && enrollInSequence,
    // A list whose tenant has no CRM connection is a configuration state, not a
    // transient failure.
    retry: false,
    // Handled locally via connection.data; no global error toast.
    meta: { suppressErrorToast: true },
  });
  const connectionId = connection.data?.connection_id ?? null;
  const sequences = useQuery({
    queryKey: keys.crmSequence.byConnection(connectionId ?? ""),
    queryFn: () => api.getConnectionSequences(connectionId!),
    enabled: open && enrollInSequence && !!connectionId,
  });

  const { data: job } = useQuery({
    queryKey: keys.prospectListCrmExportJob.latest(listId),
    queryFn: () => api.getLatestCrmExportJob(listId),
    enabled: open,
    meta: { live: true },
  });

  const isRunning = isJobActive(job?.status);

  const startExport = useMutation({
    mutationFn: () =>
      api.startCrmExport(listId, {
        scope,
        include_contacts: includeContacts,
        create_crm_list: createCrmList,
        enroll_in_sequence: enrollInSequence,
        sequence_id: enrollInSequence ? sequenceId : undefined,
      }),
    onSuccess: (created) => {
      queryClient.setQueryData(keys.prospectListCrmExportJob.latest(listId), created);
    },
  });

  const exportedTotal = (job?.companies_exported ?? 0) + (job?.contacts_exported ?? 0);
  const progress =
    job?.matched_count && job.matched_count > 0
      ? Math.min(100, Math.round((exportedTotal / job.matched_count) * 100))
      : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export to CRM</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Creates or updates the companies and contacts in this collection in the CRM connected to
            this tenant. Re-running updates the same records instead of duplicating them.
          </Typography>

          <FormControl size="small" fullWidth disabled={isRunning}>
            <InputLabel id="crm-export-scope-label">Entries to export</InputLabel>
            <Select
              labelId="crm-export-scope-label"
              label="Entries to export"
              value={scope}
              onChange={(e) => setScope(e.target.value as CrmExportScope)}
            >
              {(Object.keys(SCOPE_LABELS) as CrmExportScope[]).map((value) => (
                <MenuItem key={value} value={value}>
                  {SCOPE_LABELS[value]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={includeContacts}
                onChange={(e) => setIncludeContacts(e.target.checked)}
                disabled={isRunning}
              />
            }
            label="Include contacts, associated to their company"
          />
          <FormControlLabel
            control={
              <Switch
                checked={createCrmList}
                onChange={(e) => setCreateCrmList(e.target.checked)}
                disabled={isRunning}
              />
            }
            label="Add everything to a static list in the CRM"
          />
          <FormControlLabel
            control={
              <Switch
                checked={enrollInSequence}
                onChange={(e) => setEnrollInSequence(e.target.checked)}
                disabled={isRunning || !includeContacts}
              />
            }
            label="Enrol contacts in a sequence"
          />
          {enrollInSequence && (
            <>
              <FormControl size="small" fullWidth disabled={isRunning}>
                <InputLabel id="crm-export-sequence-label">Sequence</InputLabel>
                <Select
                  labelId="crm-export-sequence-label"
                  label="Sequence"
                  value={sequenceId}
                  onChange={(e) => setSequenceId(e.target.value)}
                >
                  {(sequences.data ?? []).map((s) => (
                    <MenuItem key={s.sequence_id} value={s.sequence_id}>
                      {s.name ?? s.sequence_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info">
                Each contact is enrolled as the CRM owner who owns them, sending from that
                person's inbox. Contacts with no owner — or an owner without a connected inbox
                — are skipped rather than sent by someone else, and listed in the result.
              </Alert>
              {connection.isError && (
                <Alert severity="warning">{getErrorMessage(connection.error)}</Alert>
              )}
              {sequences.data?.length === 0 && (
                <Alert severity="warning">
                  No sequences have been synced for this connection yet.
                </Alert>
              )}
            </>
          )}

          {startExport.error && (
            <Alert severity="error">{(startExport.error as Error).message}</Alert>
          )}

          {job && (
            <Box>
              {isRunning && (
                <>
                  <LinearProgress
                    variant={job.matched_count ? "determinate" : "indeterminate"}
                    value={progress}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {exportedTotal}
                    {job.matched_count ? ` of ${job.matched_count}` : ""} exported
                  </Typography>
                </>
              )}
              {job.status === "completed" && (
                <Alert severity="success">
                  Exported {job.companies_exported} companies and {job.contacts_exported}{" "}
                  contacts
                  {job.contacts_enrolled > 0 &&
                    `, enrolled ${job.contacts_enrolled} in the sequence`}
                  {job.skipped_count > 0 && `, skipped ${job.skipped_count}`}
                  {job.failed_count > 0 && `, ${job.failed_count} rejected by the CRM`}.
                </Alert>
              )}
              {job.status === "failed" && (
                <Alert severity="error">{job.error ?? "Export failed"}</Alert>
              )}
              {(job.result?.errors?.length ?? 0) > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {job.result.errors?.length} record(s) could not be exported. First error:{" "}
                  {job.result.errors?.[0]?.message ?? "unknown"}
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => startExport.mutate()}
          loading={isRunning || startExport.isPending}
          disabled={enrollInSequence && !sequenceId}
        >
          Start export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
